import * as React from "react";
import { Home, Users, Flag, BarChart2, LogOut, ShieldCheck, ClipboardList } from "lucide-react";
import { NavUser } from "./nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarGroup,
  SidebarGroupContent,
} from "./ui/sidebar";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AdminService from "../sevices/admin.service";
import { toastSuccess } from "./common/toastNotification";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const adminUser = {
  name: "Admin",
  email: "admin@gmail.com",
  avatar: "",
};

export function AppSidebar({ ...props }) {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();

  const [badgeCounts, setBadgeCounts] = React.useState({
    pendingVerifications: 0,
    pendingReports: 0,
  });

  const [seenCounts, setSeenCounts] = React.useState(() => {
    try {
      return {
        verification: parseInt(localStorage.getItem("signova_seen_verification") || "0", 10),
        reports: parseInt(localStorage.getItem("signova_seen_reports") || "0", 10),
      };
    } catch {
      return { verification: 0, reports: 0 };
    }
  });

  const fetchBadges = React.useCallback(async () => {
    try {
      const res = await AdminService.getBadgeCounts();
      if (res?.data?.data) {
        const pVerifications = Number(res.data.data.pendingVerifications || 0);
        const pReports = Number(res.data.data.pendingReports || 0);

        setBadgeCounts({
          pendingVerifications: pVerifications,
          pendingReports: pReports,
        });

        // Auto-reconcile seenCounts if total pending is now lower than seen (e.g. admin approved/resolved items)
        setSeenCounts((prev) => {
          let updated = false;
          let newVerif = prev.verification;
          let newReports = prev.reports;

          if (newVerif > pVerifications) {
            newVerif = pVerifications;
            localStorage.setItem("signova_seen_verification", String(newVerif));
            updated = true;
          }
          if (newReports > pReports) {
            newReports = pReports;
            localStorage.setItem("signova_seen_reports", String(newReports));
            updated = true;
          }

          return updated ? { verification: newVerif, reports: newReports } : prev;
        });
      }
    } catch {
      // Quiet fail if network/auth not ready
    }
  }, []);

  // Fetch immediately on mount, on route change, on window focus, and on fast 5s interval
  React.useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 5000); // 5s fast polling for real-time reactivity without refresh

    const handleFocus = () => fetchBadges();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchBadges();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("refreshBadgeCounts", fetchBadges);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("refreshBadgeCounts", fetchBadges);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchBadges]);

  // When visiting /verification or /reports, automatically mark current count as seen
  React.useEffect(() => {
    fetchBadges(); // Refresh immediately upon navigating

    if (pathname === "/verification" || pathname.startsWith("/verification/")) {
      setBadgeCounts((currentBadges) => {
        const current = currentBadges.pendingVerifications;
        localStorage.setItem("signova_seen_verification", String(current));
        setSeenCounts((prev) => ({ ...prev, verification: current }));
        return currentBadges;
      });
    } else if (pathname === "/reports" || pathname.startsWith("/reports/")) {
      setBadgeCounts((currentBadges) => {
        const current = currentBadges.pendingReports;
        localStorage.setItem("signova_seen_reports", String(current));
        setSeenCounts((prev) => ({ ...prev, reports: current }));
        return currentBadges;
      });
    }
  }, [pathname, fetchBadges]);

  const handleNavClick = (url) => {
    if (url === "/verification") {
      const current = badgeCounts.pendingVerifications;
      localStorage.setItem("signova_seen_verification", String(current));
      setSeenCounts((prev) => ({ ...prev, verification: current }));
    } else if (url === "/reports") {
      const current = badgeCounts.pendingReports;
      localStorage.setItem("signova_seen_reports", String(current));
      setSeenCounts((prev) => ({ ...prev, reports: current }));
    }
  };

  const [isLogoutOpen, setIsLogoutOpen] = React.useState(false);

  const handleLogout = () => {
    setIsLogoutOpen(true);
  };

  const handleLogoutConfirm = () => {
    try {
      localStorage.removeItem("signova-admin");
      sessionStorage.clear();
    } catch {
      // ignore
    }
    setIsLogoutOpen(false);
    toastSuccess("Logged out successfully");
    navigate("/login");
  };

  // Compute unread amounts (if currently on that page, show 0)
  const isVerificationPage = pathname === "/verification" || pathname.startsWith("/verification/");
  const isReportsPage = pathname === "/reports" || pathname.startsWith("/reports/");

  const unreadVerification = isVerificationPage
    ? 0
    : Math.max(0, badgeCounts.pendingVerifications - (seenCounts.verification || 0));

  const unreadReports = isReportsPage
    ? 0
    : Math.max(0, badgeCounts.pendingReports - (seenCounts.reports || 0));

  const navItems = [
    { title: "Dashboard", url: "/dashboard", icon: Home },
    { title: "Users", url: "/users", icon: Users },
    {
      title: "Reports",
      url: "/reports",
      icon: Flag,
      badgeCount: unreadReports,
      badgeColor: "bg-rose-500",
    },
    {
      title: "Verification",
      url: "/verification",
      icon: ShieldCheck,
      badgeCount: unreadVerification,
      badgeColor: "bg-purple-600",
    },
    { title: "Analytics", url: "/analytics", icon: BarChart2 },
    { title: "Activity Log", url: "/activity-log", icon: ClipboardList },
  ];

  return (
    <>
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to="/dashboard">
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg overflow-hidden bg-white shadow-sm">
                    <img src="/logo.png" alt="Signova" className="size-8 object-contain" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">Signova Admin</span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => {
                  const isActive = pathname === item.url || pathname.startsWith(item.url + "/");
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={isActive}>
                        <Link
                          to={item.url}
                          onClick={() => handleNavClick(item.url)}
                          className="flex items-center justify-between w-full"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <item.icon className="size-4 shrink-0" />
                            <span className="truncate">{item.title}</span>
                          </div>
                          {item.badgeCount > 0 && (
                            <span
                              className={`ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold text-white ${item.badgeColor} rounded-full shadow-sm animate-in fade-in zoom-in duration-150`}
                            >
                              {item.badgeCount > 99 ? "99+" : item.badgeCount}
                            </span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup className="mt-auto">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={handleLogout}
                    className="w-full text-red-600 bg-red-50/80 border border-red-200/80 hover:bg-red-100 hover:text-red-700 font-semibold cursor-pointer transition-all"
                  >
                    <LogOut className="text-red-600 size-4" />
                    <span>Logout</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <NavUser user={adminUser} />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <AlertDialog open={isLogoutOpen} onOpenChange={setIsLogoutOpen}>
        <AlertDialogContent className="max-w-md bg-white p-6 rounded-2xl shadow-2xl border border-gray-100">
          <AlertDialogHeader className="flex flex-col items-center text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 shadow-inner">
              <LogOut className="w-6 h-6" />
            </div>
            <AlertDialogTitle className="text-xl font-bold text-gray-900">
              Confirm Logout
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500 leading-relaxed max-w-xs text-center">
              Are you sure you want to log out from the Signova Admin Panel? You will need to sign in again to access the dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-row items-center justify-center gap-3 mt-6 sm:justify-center">
            <AlertDialogCancel className="w-1/2 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-100 font-medium transition-all">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogoutConfirm}
              className="w-1/2 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md hover:shadow-lg transition-all"
            >
              Yes, Log Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
